<template>
  <header>
    <a-form class="form" layout="vertical" ref="formRef" :model="formState" :rules="rules">
      <a-form-item v-if="user?.customer?.type_business == 1" label="Empresa" name="entity" required>
        <entity-picker
          :showAll="false"
          defaultBehavior
          :value="formState.entity"
          @update:value="formState.entity = $event"
        />
      </a-form-item>

      <a-form-item label="Campaña" name="portfolio" required>
        <campaign-picker
          :showAll="false"
          :value="formState.portfolio"
          @update:value="formState.portfolio = $event"
          :entity="formState.entity"
        />
      </a-form-item>

      <a-form-item v-if="user?.customer?.enable_bases" label="Base" name="base" required>
        <bases-picker
          defaultBehavior
          :value="formState.base"
          @update:value="formState.base = $event"
          :entity="formState.entity"
          :portfolio="formState.portfolio"
        />
      </a-form-item>

      <a-form-item label="Mes" name="month" required>
        <a-month-picker
          v-model:value="formState.month"
          :disabled-date="disableStartDate"
          format="MM/YYYY"
          valueFormat="YYYY-MM"
          placeholder="-- / ----"
          @openChange="startOpenChange"
        />
      </a-form-item>
    </a-form>

    <div class="actions">
      <a-tooltip placement="top">
        <template #title>
          <span v-if="isGlobalDownloading">Tiene una descarga pendiente</span>
          <span v-else>
            Se descargarán únicamente los datos de la tabla de acuerdo a los filtros seleccionados.
          </span>
        </template>

        <a-button
          type="link"
          :disabled="isButtonDisabled || isGlobalDownloading"
          :loading="isDownloading"
          @click="handleDownload"
        >
          Descargar
          <DownloadOutlined />
        </a-button>
      </a-tooltip>

      <a-button type="primary" :disabled="isButtonDisabled || isReportLoading" :loading="isReportLoading" @click="handleSearch">
        Buscar
        <SearchOutlined />
      </a-button>
    </div>
  </header>

  <CustomScrollbar height="calc(100vh - 312px)">
    <a-spin v-if="formState.month" :spinning="isReportLoading">
      <DailyManagementChart
        title="Gráfico de gestiones diarias"
        :labels="chartLabels"
        :data="reportData"
      />

      <a-table
        class="table"
        size="middle"
        :columns="tableColumns"
        :data-source="reportData"
        :scroll="{ x: 1800 }"
        :pagination="false"
      >
        <template #dayContent="{ text }">
          {{ text ?? 0 }}
        </template>

        <template #totalContent="{ text }">
          {{ String(text).length > 4 ? text?.toFixed(2) : text }}
        </template>
      </a-table>
    </a-spin>
  </CustomScrollbar>
</template>

<script setup>
import { computed, reactive, ref } from "vue";
import { useStore } from "vuex";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons-vue";

import { fetchDailyManagementType, downloadDailyManagementType } from "@/app/reports/services";
import { getDailyManagementColumns } from "@/app/reports/utils/tableColumns/customerSupport";
import { getChartLabels } from "@/app/reports/utils/chart";
import { formatDailyManagementData } from "@/app/reports/utils/helpers";
import { useGlobalDownloadState } from "@/app/shared/composables/useGlobalDownloadState";

import EntityPicker from "@/app/shared/components/pickers/EntityPicker.vue";
import CampaignPicker from "@/app/shared/components/pickers/CampaignPicker.vue";
import BasesPicker from "@/app/shared/components/pickers/BasesPicker.vue";
import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";
import DailyManagementChart from "./charts/DailyManagementChart.vue";

const store = useStore();

const user = computed(() => store.getters["getDataUser"]);

// Form
const formState = reactive({
  portfolio: null,
  month: null,
});
const isButtonDisabled = computed(() => {
  for (const key in formState) {
    if (!formState[key]) return true;
  }

  return false;
});
const isDownloading = ref(false);
const { isGlobalDownloading } = useGlobalDownloadState();

// Chart
const chartLabels = computed(() => getChartLabels(formState.month || ""));

// Table
const reportData = ref(null);
const isReportLoading = ref(false);
const tableColumns = ref([]);

const fetchReportData = async () => {
  isReportLoading.value = true;

  const response = await fetchDailyManagementType({ filters: formState });
  reportData.value = formatDailyManagementData({ rawData: response?.data });
  tableColumns.value = getDailyManagementColumns(formState.month || "");

  isReportLoading.value = false;
};

const handleSearch = async () => {
  await fetchReportData();
};

const handleDownload = async () => {
  isDownloading.value = true;
  await downloadDailyManagementType({ filters: formState });
  isDownloading.value = false;
};
</script>

<style lang="sass" scoped>
header
  display: flex
  justify-content: space-between
  align-items: flex-end
  margin: 15px 0

.form
  display: flex
  gap: 10px

.ant-form-item
  margin: 0
  width: 180px

:deep(.ant-form-item-label)
  padding: 0

.actions
  display: flex
  align-items: center
  gap: 10px

.table
  margin-top: 15px
</style>
